import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Grid, TextField, Button,
  MenuItem, Select, FormControl, InputLabel, IconButton, Alert,
  LinearProgress, Tabs, Tab, Tooltip as MuiTooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, TablePagination, Stack,
} from '@mui/material';
import {
  Science, Refresh, RestartAlt, Delete, PlayArrow, CheckCircle,
  TrendingUp, TrendingDown, ShowChart, ShoppingCart, BugReport,
  Storage, FilterList, Download, MonetizationOn, Speed, Security,
  FiberManualRecord, Assessment, Close, Check, Send
} from '@mui/icons-material';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import toast from 'react-hot-toast';
import dayjs from '../utils/dayjs';
import { testingApi, testPositions, trading } from '../services/api';

const fmtMoney = (v) => parseFloat(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const pnlColorOf = (v) => (parseFloat(v) >= 0 ? '#10b981' : '#ef4444');

const SYMBOL_PRESETS = ['NIFTY', 'BANKNIFTY', 'RELIANCE', 'TCS', 'HDFCBANK', 'INFY', 'SBIN', 'BHARTIARTL', 'TATASTEEL'];

export default function TestingModulePage() {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Dashboard Data from Backend
  const [dashData, setDashData] = useState(null);

  // All Orders Data & Pagination
  const [orders, setOrders] = useState([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Filters for All Orders
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [modeFilter, setModeFilter] = useState('ALL');
  const [assetFilter, setAssetFilter] = useState('ALL');

  // Sandbox State
  const [sandboxPositions, setSandboxPositions] = useState([]);
  const [priceInputs, setPriceInputs] = useState({});
  const [updatingId, setUpdatingId] = useState(null);
  const [sandboxForm, setSandboxForm] = useState({
    symbol: 'NIFTY',
    label: '',
    entryPrice: '',
    slPct: '2.0',
    targetPct: '5.0',
  });
  const [creatingSandbox, setCreatingSandbox] = useState(false);

  // Quick Simulation Dialog
  const [simModalOpen, setSimModalOpen] = useState(false);
  const [simForm, setSimForm] = useState({
    symbol: 'SBIN',
    asset_type: 'EQUITY',
    quantity: 5,
    price: '',
  });
  const [simulating, setSimulating] = useState(false);

  // Fetch Dashboard Stats
  const loadDashboard = useCallback(async () => {
    try {
      const res = await testingApi.dashboard();
      if (res.data?.success) {
        setDashData(res.data.data);
        setSandboxPositions(res.data.data?.testPositions || []);
      }
    } catch {
      // Handled by interceptor
    }
  }, []);

  // Fetch All Orders
  const loadAllOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        search,
        status: statusFilter,
        mode: modeFilter,
        asset: assetFilter,
      };
      const res = await testingApi.allOrders(params);
      if (res.data?.success) {
        setOrders(res.data.data || []);
        setTotalOrders(res.data.total || 0);
      }
    } catch {
      // Handled
    } finally {
      setOrdersLoading(false);
    }
  }, [page, rowsPerPage, search, statusFilter, modeFilter, assetFilter]);

  const loadAll = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([loadDashboard(), loadAllOrders()]);
    setRefreshing(false);
    setLoading(false);
  }, [loadDashboard, loadAllOrders]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Sandbox Actions
  const handleCreateSandbox = async () => {
    if (!sandboxForm.entryPrice || parseFloat(sandboxForm.entryPrice) <= 0) {
      toast.error('Please enter a valid entry price');
      return;
    }
    setCreatingSandbox(true);
    try {
      await testPositions.create(sandboxForm);
      toast.success(`Sandbox position created for ${sandboxForm.symbol}`);
      setSandboxForm({ ...sandboxForm, entryPrice: '', label: '' });
      loadAll();
    } catch {
      toast.error('Failed to create test position');
    } finally {
      setCreatingSandbox(false);
    }
  };

  const handleUpdatePrice = async (id) => {
    const price = priceInputs[id];
    if (!price || parseFloat(price) <= 0) {
      toast.error('Enter a valid price');
      return;
    }
    setUpdatingId(id);
    try {
      const res = await testPositions.updatePrice(id, price);
      const d = res.data?.data;
      if (d?.status === 'CLOSED') {
        toast.error(`Exit Triggered: Closed @ ₹${d.current_price} (${d.exit_reason})`);
      } else if (d?.slRaised) {
        toast.success(`Trailing SL Ratcheted Up → ₹${d.stop_loss}`);
      } else {
        toast(`Price updated to ₹${price} (SL at ₹${d.stop_loss})`);
      }
      setPriceInputs(prev => ({ ...prev, [id]: '' }));
      loadAll();
    } catch {
      toast.error('Failed to update price');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDeleteSandbox = async (id) => {
    try {
      await testPositions.remove(id);
      toast.success('Position deleted');
      loadAll();
    } catch {
      toast.error('Failed to delete position');
    }
  };

  const handleResetSandbox = async () => {
    if (!window.confirm('Clear ALL sandbox test positions?')) return;
    try {
      await testPositions.resetAll();
      toast.success('Test sandbox cleared');
      loadAll();
    } catch {
      toast.error('Failed to clear sandbox');
    }
  };

  // Quick Simulation Action
  const handleSimulateTrade = async () => {
    setSimulating(true);
    try {
      const res = await testingApi.simulateTrade(simForm);
      if (res.data?.success) {
        toast.success(res.data.message || 'Simulated order executed successfully');
        setSimModalOpen(false);
        setSimForm({ symbol: 'SBIN', asset_type: 'EQUITY', quantity: 5, price: '' });
        loadAll();
      }
    } catch {
      toast.error('Failed to simulate order');
    } finally {
      setSimulating(false);
    }
  };

  // Export CSV
  const handleExportCSV = () => {
    if (!orders || orders.length === 0) {
      toast.error('No orders available to export');
      return;
    }
    const headers = ['Order ID', 'Symbol', 'Contract', 'Asset', 'Mode', 'Side', 'Qty', 'Entry Price', 'Exit Price', 'P&L (INR)', 'P&L (%)', 'Status', 'Exit Reason', 'Placed At', 'Closed At'];
    const rows = orders.map(o => [
      o.id,
      o.symbol,
      `"${o.contract_label || o.name}"`,
      o.asset_type,
      o.mode,
      o.order_side,
      o.quantity,
      o.entry_price,
      o.exit_price || '',
      o.pnl,
      o.pnl_pct,
      o.status,
      o.exit_reason || '',
      o.placed_at ? dayjs(o.placed_at).format('YYYY-MM-DD HH:mm:ss') : '',
      o.closed_at ? dayjs(o.closed_at).format('YYYY-MM-DD HH:mm:ss') : '',
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `all_orders_export_${dayjs().format('YYYYMMDD_HHmmss')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Orders data exported to CSV');
  };

  const summary = dashData?.summary || {};
  const market = dashData?.market || {};
  const charts = dashData?.charts || {};

  return (
    <Box sx={{ pb: 6 }}>
      {/* ── Top Header & Action Controls ──────────────────────────────────── */}
      <Card sx={{
        mb: 3,
        background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
        color: '#ffffff',
        borderRadius: 3,
        border: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.2)',
      }}>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={2} alignItems="center" justifyContent="space-between">
            <Grid item xs={12} md={7}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                <Box sx={{
                  p: 1.2,
                  bgcolor: 'rgba(37, 99, 235, 0.25)',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  border: '1px solid rgba(59, 130, 246, 0.4)'
                }}>
                  <BugReport sx={{ color: '#60a5fa', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: '-0.02em' }}>
                    Testing &amp; Orders Terminal
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                    Multi-engine order tracking across Stock Equities, Options, Sandbox &amp; Trade History
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1.5, gap: 1 }}>
                <Chip
                  size="small"
                  icon={<FiberManualRecord sx={{ fontSize: '10px !important', color: market.isOpen ? '#10b981' : '#ef4444' }} />}
                  label={market.isOpen ? 'LIVE MARKET OPEN (09:15 - 15:30 IST)' : 'EXCHANGE CLOSED'}
                  sx={{
                    bgcolor: market.isOpen ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                    color: market.isOpen ? '#34d399' : '#f87171',
                    border: `1px solid ${market.isOpen ? 'rgba(16, 185, 129, 0.4)' : 'rgba(239, 68, 68, 0.4)'}`,
                    fontWeight: 700,
                  }}
                />
                <Chip
                  size="small"
                  label={`BOT: ${market.botRunning ? 'ACTIVE' : 'IDLE'} (${market.tradeMode})`}
                  sx={{
                    bgcolor: market.botRunning ? 'rgba(59, 130, 246, 0.2)' : 'rgba(148, 163, 184, 0.2)',
                    color: market.botRunning ? '#60a5fa' : '#cbd5e1',
                    fontWeight: 700,
                  }}
                />
                <Chip
                  size="small"
                  label={`TOTAL ORDERS: ${summary.totalAllOrders || 0}`}
                  sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', color: '#f1f5f9', fontWeight: 700 }}
                />
              </Stack>
            </Grid>

            <Grid item xs={12} md={5}>
              <Stack direction="row" spacing={1.5} justifyContent={{ xs: 'flex-start', md: 'flex-end' }} flexWrap="wrap">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Refresh className={refreshing ? 'animate-spin' : ''} />}
                  onClick={loadAll}
                  disabled={refreshing}
                  sx={{
                    color: '#ffffff',
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                    '&:hover': { borderColor: '#ffffff', bgcolor: 'rgba(255, 255, 255, 0.05)' }
                  }}
                >
                  Refresh
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  color="primary"
                  startIcon={<PlayArrow />}
                  onClick={() => setSimModalOpen(true)}
                  sx={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    fontWeight: 700,
                    boxShadow: '0 4px 14px rgba(37, 99, 235, 0.4)',
                  }}
                >
                  Simulate Order
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Download />}
                  onClick={handleExportCSV}
                  sx={{
                    color: '#34d399',
                    borderColor: 'rgba(16, 185, 129, 0.4)',
                    '&:hover': { borderColor: '#10b981', bgcolor: 'rgba(16, 185, 129, 0.1)' }
                  }}
                >
                  Export CSV
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ── KPI Metric Cards ──────────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Total Orders Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            borderRadius: 3,
            p: 2,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
            border: '1px solid #e2e8f0',
            position: 'relative',
            overflow: 'hidden',
          }}>
            <Box sx={{
              position: 'absolute', right: -10, top: -10, width: 80, height: 80,
              bgcolor: 'rgba(37, 99, 235, 0.05)', borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <Storage sx={{ color: '#2563eb', fontSize: 40, opacity: 0.3 }} />
            </Box>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Total Orders Tracked
            </Typography>
            <Typography variant="h4" fontWeight={800} sx={{ my: 0.5, color: '#0f172a' }}>
              {summary.totalAllOrders || 0}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip size="small" label={`Equities: ${summary.equitySoldCount + (summary.equityOpenCount || 0)}`} sx={{ fontSize: 10, height: 20, bgcolor: '#f1f5f9', fontWeight: 600 }} />
              <Chip size="small" label={`Options: ${summary.paperPositionsOpen || 0}`} sx={{ fontSize: 10, height: 20, bgcolor: '#f1f5f9', fontWeight: 600 }} />
              <Chip size="small" label={`Sandbox: ${summary.testPositionsCount || 0}`} sx={{ fontSize: 10, height: 20, bgcolor: '#f1f5f9', fontWeight: 600 }} />
            </Stack>
          </Card>
        </Grid>

        {/* Active Open Positions Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            borderRadius: 3,
            p: 2,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
            border: '1px solid #e2e8f0',
            position: 'relative',
            overflow: 'hidden',
          }}>
            <Box sx={{
              position: 'absolute', right: -10, top: -10, width: 80, height: 80,
              bgcolor: 'rgba(16, 185, 129, 0.05)', borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <TrendingUp sx={{ color: '#10b981', fontSize: 40, opacity: 0.3 }} />
            </Box>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Active / Running Orders
            </Typography>
            <Typography variant="h4" fontWeight={800} sx={{ my: 0.5, color: '#10b981' }}>
              {summary.totalOpenPositions || 0}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              Capital Deployed: <b>₹{fmtMoney(summary.totalCapitalDeployed)}</b>
            </Typography>
          </Card>
        </Grid>

        {/* Realized P&L Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            borderRadius: 3,
            p: 2,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
            border: '1px solid #e2e8f0',
            position: 'relative',
            overflow: 'hidden',
          }}>
            <Box sx={{
              position: 'absolute', right: -10, top: -10, width: 80, height: 80,
              bgcolor: (summary.totalRealizedPnl || 0) >= 0 ? 'rgba(16, 185, 129, 0.05)' : 'rgba(239, 68, 68, 0.05)', borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <MonetizationOn sx={{ color: pnlColorOf(summary.totalRealizedPnl), fontSize: 40, opacity: 0.3 }} />
            </Box>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Total Realized P&amp;L
            </Typography>
            <Typography variant="h4" fontWeight={800} sx={{ my: 0.5, color: pnlColorOf(summary.totalRealizedPnl) }}>
              {(summary.totalRealizedPnl || 0) >= 0 ? '+' : ''}₹{fmtMoney(summary.totalRealizedPnl)}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              {summary.winningTrades || 0} Wins · {summary.losingTrades || 0} Losses (Brokerage: ₹{fmtMoney(summary.totalBrokerage)})
            </Typography>
          </Card>
        </Grid>

        {/* Win Rate Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            borderRadius: 3,
            p: 2,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
            border: '1px solid #e2e8f0',
            position: 'relative',
            overflow: 'hidden',
          }}>
            <Box sx={{
              position: 'absolute', right: -10, top: -10, width: 80, height: 80,
              bgcolor: 'rgba(99, 102, 241, 0.05)', borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <Speed sx={{ color: '#6366f1', fontSize: 40, opacity: 0.3 }} />
            </Box>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Win Rate Accuracy
            </Typography>
            <Typography variant="h4" fontWeight={800} sx={{ my: 0.5, color: '#6366f1' }}>
              {summary.winRatePct || 0}%
            </Typography>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, Math.max(0, summary.winRatePct || 0))}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: '#f1f5f9',
                '& .MuiLinearProgress-bar': { bgcolor: '#6366f1' }
              }}
            />
          </Card>
        </Grid>
      </Grid>

      {/* ── Interactive Charts Section ────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Cumulative PnL Trajectory Chart */}
        <Grid item xs={12} md={8}>
          <Card sx={{ borderRadius: 3, p: 2.5, boxShadow: '0 4px 12px rgba(0,0,0,0.04)', border: '1px solid #e2e8f0' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ShowChart color="primary" /> Cumulative Performance Curve
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Realized profit/loss trajectory across all closed trades over time
                </Typography>
              </Box>
              <Chip
                size="small"
                label={`${(charts.pnlCurve || []).length} Data Points`}
                sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}
              />
            </Box>

            <Box sx={{ width: '100%', height: 260 }}>
              {charts.pnlCurve && charts.pnlCurve.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={charts.pnlCurve} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="pnlGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#2563eb" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#2563eb" stopOpacity={0.0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${v}`} />
                    <Tooltip
                      formatter={(val, name, props) => [
                        `₹${parseFloat(val).toLocaleString('en-IN')}`,
                        name === 'cumPnl' ? 'Cumulative P&L' : 'Trade P&L'
                      ]}
                      labelFormatter={(l, p) => p?.[0]?.payload ? `${p[0].payload.symbol} (${p[0].payload.date})` : l}
                    />
                    <Area type="monotone" dataKey="cumPnl" stroke="#2563eb" strokeWidth={2.5} fillOpacity={1} fill="url(#pnlGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="body2" color="text.secondary">No performance curve data available yet</Typography>
                </Box>
              )}
            </Box>
          </Card>
        </Grid>

        {/* Order Status Distribution Pie Chart */}
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3, p: 2.5, boxShadow: '0 4px 12px rgba(0,0,0,0.04)', border: '1px solid #e2e8f0', height: '100%' }}>
            <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Assessment color="secondary" /> Order Status Distribution
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              Breakdown by Active, Target Hit, SL Hit &amp; Failed
            </Typography>

            <Box sx={{ width: '100%', height: 230 }}>
              {charts.statusDistribution && charts.statusDistribution.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={charts.statusDistribution}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={75}
                      paddingAngle={3}
                    >
                      {charts.statusDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(val) => [`${val} orders`, 'Count']} />
                    <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="body2" color="text.secondary">No distribution data</Typography>
                </Box>
              )}
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* ── Main Tabbed Section ───────────────────────────────────────────── */}
      <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.04)' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 1, bgcolor: '#f8fafc' }}>
          <Tabs
            value={activeTab}
            onChange={(_, val) => setActiveTab(val)}
            sx={{
              '& .MuiTab-root': {
                fontWeight: 700,
                fontSize: '0.875rem',
                textTransform: 'none',
                minHeight: 48,
              }
            }}
          >
            <Tab icon={<Storage sx={{ fontSize: 18 }} />} iconPosition="start" label={`🌐 All Orders & Trades (${totalOrders})`} />
            <Tab icon={<Science sx={{ fontSize: 18 }} />} iconPosition="start" label={`🧪 Sandbox Trailing SL Tester (${sandboxPositions.length})`} />
          </Tabs>
        </Box>

        {/* ── Tab 0: Unified All Orders & Trades Table ─────────────────────── */}
        {activeTab === 0 && (
          <Box sx={{ p: 2.5 }}>
            {/* Filter Bar */}
            <Grid container spacing={2} alignItems="center" sx={{ mb: 2.5 }}>
              <Grid item xs={12} sm={4} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by Symbol, ID, Exit Reason..."
                  value={search}
                  onChange={e => { setSearch(e.target.value); setPage(0); }}
                />
              </Grid>
              <Grid item xs={6} sm={2} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select value={statusFilter} label="Status" onChange={e => { setStatusFilter(e.target.value); setPage(0); }}>
                    <MenuItem value="ALL">All Statuses</MenuItem>
                    <MenuItem value="OPEN">🟢 Open / Running</MenuItem>
                    <MenuItem value="SOLD">🔵 Sold / Closed</MenuItem>
                    <MenuItem value="FAILED">🔴 Failed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={2} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Mode</InputLabel>
                  <Select value={modeFilter} label="Mode" onChange={e => { setModeFilter(e.target.value); setPage(0); }}>
                    <MenuItem value="ALL">All Modes</MenuItem>
                    <MenuItem value="PAPER">📄 Paper Trading</MenuItem>
                    <MenuItem value="LIVE">🚀 Live Trading</MenuItem>
                    <MenuItem value="TEST">🧪 Sandbox Test</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={2} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Asset</InputLabel>
                  <Select value={assetFilter} label="Asset" onChange={e => { setAssetFilter(e.target.value); setPage(0); }}>
                    <MenuItem value="ALL">All Assets</MenuItem>
                    <MenuItem value="EQUITY">📦 Stock Equities</MenuItem>
                    <MenuItem value="OPTION">📈 Index Options</MenuItem>
                    <MenuItem value="SANDBOX">🧪 Sandbox</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={2} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  size="medium"
                  startIcon={<Refresh />}
                  onClick={loadAllOrders}
                  sx={{ height: 40 }}
                >
                  Apply
                </Button>
              </Grid>
            </Grid>

            {/* Table Container */}
            <TableContainer sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
              {ordersLoading && <LinearProgress />}
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 800, bgcolor: '#f8fafc', py: 1.2, color: '#475569' } }}>
                    <TableCell>Order ID / Tag</TableCell>
                    <TableCell>Date &amp; Time</TableCell>
                    <TableCell>Asset / Contract</TableCell>
                    <TableCell align="center">Type</TableCell>
                    <TableCell align="center">Mode</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Entry</TableCell>
                    <TableCell align="right">Current / Exit</TableCell>
                    <TableCell align="right">SL / Target</TableCell>
                    <TableCell align="right">P&amp;L (Return)</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell>Exit Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No orders found matching the selected filters.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    orders.map((o) => {
                      const isOpen = o.status === 'OPEN' || o.status === 'PLACED';
                      const isSold = o.status === 'SOLD' || o.status === 'CLOSED';
                      const pnl = parseFloat(o.pnl || 0);
                      const pnlColor = pnlColorOf(pnl);

                      return (
                        <TableRow key={o.id} hover sx={{ '& td': { py: 1 } }}>
                          <TableCell>
                            <Chip
                              size="small"
                              label={o.id}
                              sx={{
                                fontWeight: 700,
                                fontSize: 11,
                                height: 22,
                                bgcolor: o.asset_type === 'EQUITY' ? '#eff6ff' : (o.asset_type === 'OPTION' ? '#f5f3ff' : '#ecfdf5'),
                                color: o.asset_type === 'EQUITY' ? '#2563eb' : (o.asset_type === 'OPTION' ? '#7c3aed' : '#059669'),
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <Typography variant="body2" fontWeight={600}>
                              {o.placed_at ? dayjs(o.placed_at).format('DD/MM/YY') : '—'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {o.placed_at ? dayjs(o.placed_at).format('HH:mm:ss') : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={700}>
                              {o.symbol}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {o.contract_label || o.name}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              size="small"
                              label={o.product || o.asset_type}
                              sx={{
                                fontWeight: 700,
                                fontSize: 10,
                                height: 20,
                                bgcolor: o.asset_type === 'EQUITY' ? '#e0f2fe' : '#f3e8ff',
                                color: o.asset_type === 'EQUITY' ? '#0284c7' : '#9333ea',
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              size="small"
                              label={o.mode}
                              sx={{
                                fontWeight: 700,
                                fontSize: 10,
                                height: 20,
                                bgcolor: o.mode === 'LIVE' ? '#fee2e2' : '#e0e7ff',
                                color: o.mode === 'LIVE' ? '#dc2626' : '#4338ca',
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <b>{o.quantity}</b> {o.lot_size > 1 ? `(${o.quantity * o.lot_size})` : ''}
                          </TableCell>
                          <TableCell align="right">
                            ₹{fmtMoney(o.entry_price)}
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={700}>
                              ₹{fmtMoney(o.exit_price || o.current_price)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="caption" display="block" color="error.main">
                              SL: ₹{o.stop_loss ? fmtMoney(o.stop_loss) : '—'}
                            </Typography>
                            <Typography variant="caption" display="block" color="success.main">
                              Tgt: ₹{o.target ? fmtMoney(o.target) : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={800} sx={{ color: pnlColor }}>
                              {pnl >= 0 ? '+' : ''}₹{fmtMoney(pnl)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: pnlColor, fontWeight: 700 }}>
                              ({o.pnl_pct >= 0 ? '+' : ''}{parseFloat(o.pnl_pct || 0).toFixed(2)}%)
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              size="small"
                              label={o.status}
                              sx={{
                                fontWeight: 800,
                                fontSize: 10,
                                height: 22,
                                bgcolor: isOpen ? '#dcfce7' : (isSold ? '#dbeafe' : '#f1f5f9'),
                                color: isOpen ? '#15803d' : (isSold ? '#1d4ed8' : '#64748b'),
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569' }}>
                              {o.exit_reason || '—'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination Controls */}
            <TablePagination
              component="div"
              count={totalOrders}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{ mt: 1 }}
            />
          </Box>
        )}

        {/* ── Tab 1: Interactive Sandbox Trailing SL Tester ────────────────── */}
        {activeTab === 1 && (
          <Box sx={{ p: 2.5 }}>
            <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
              <b>Interactive Trailing Stop-Loss &amp; Target Simulator:</b> Type any simulated price into the box below and click Update.
              Watch the exact step-trailing stop-loss algorithm ratchet up in real-time or trigger target exits, completely isolated from real broker orders.
            </Alert>

            {/* Create Sandbox Position Card */}
            <Card sx={{ borderRadius: 2, mb: 3, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1.5 }}>
                  Create New Test Position
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Symbol</InputLabel>
                      <Select
                        value={sandboxForm.symbol}
                        label="Symbol"
                        onChange={e => setSandboxForm({ ...sandboxForm, symbol: e.target.value })}
                      >
                        {SYMBOL_PRESETS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Entry Price (₹)"
                      type="number"
                      placeholder="e.g. 1000"
                      value={sandboxForm.entryPrice}
                      onChange={e => setSandboxForm({ ...sandboxForm, entryPrice: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Initial SL (%)"
                      type="number"
                      value={sandboxForm.slPct}
                      onChange={e => setSandboxForm({ ...sandboxForm, slPct: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Target (%)"
                      type="number"
                      value={sandboxForm.targetPct}
                      onChange={e => setSandboxForm({ ...sandboxForm, targetPct: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      onClick={handleCreateSandbox}
                      disabled={creatingSandbox}
                      sx={{ height: 40, fontWeight: 700 }}
                    >
                      {creatingSandbox ? 'Creating...' : '+ Add Test'}
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Sandbox Positions Table */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={800}>
                Active Test Positions ({sandboxPositions.length})
              </Typography>
              <Button size="small" color="error" startIcon={<RestartAlt />} onClick={handleResetSandbox}>
                Reset Sandbox
              </Button>
            </Box>

            <TableContainer sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 800, bgcolor: '#f8fafc', py: 1.2 } }}>
                    <TableCell>Symbol</TableCell>
                    <TableCell align="right">Entry Price</TableCell>
                    <TableCell align="right">Current Price</TableCell>
                    <TableCell align="right">Trailing Peak</TableCell>
                    <TableCell align="right">Stop Loss</TableCell>
                    <TableCell align="right">Target</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell>Test New Price (Simulator)</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sandboxPositions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No sandbox test positions active. Create one using the form above!
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    sandboxPositions.map(p => {
                      const isOpen = p.status === 'OPEN';
                      return (
                        <TableRow key={p.id} hover sx={{ bgcolor: isOpen ? 'inherit' : '#fdf2f8' }}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={800}>{p.symbol}</Typography>
                            {p.label && <Typography variant="caption" color="text.secondary">{p.label}</Typography>}
                          </TableCell>
                          <TableCell align="right">₹{fmtMoney(p.entry_price)}</TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={700}>₹{fmtMoney(p.current_price)}</Typography>
                          </TableCell>
                          <TableCell align="right">₹{fmtMoney(p.trailing_high)}</TableCell>
                          <TableCell align="right">
                            <Chip
                              size="small"
                              label={`₹${fmtMoney(p.stop_loss)}`}
                              sx={{ bgcolor: '#fee2e2', color: '#dc2626', fontWeight: 800 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              size="small"
                              label={`₹${fmtMoney(p.target)}`}
                              sx={{ bgcolor: '#dcfce7', color: '#16a34a', fontWeight: 800 }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              size="small"
                              label={isOpen ? 'OPEN' : `CLOSED · ${p.exit_reason || 'EXIT'}`}
                              color={isOpen ? 'success' : 'error'}
                              sx={{ fontWeight: 800, fontSize: 10 }}
                            />
                          </TableCell>
                          <TableCell sx={{ minWidth: 200 }}>
                            {isOpen ? (
                              <Stack direction="row" spacing={0.5}>
                                <TextField
                                  size="small"
                                  type="number"
                                  placeholder="New Price"
                                  value={priceInputs[p.id] || ''}
                                  onChange={e => setPriceInputs(prev => ({ ...prev, [p.id]: e.target.value }))}
                                  onKeyDown={e => e.key === 'Enter' && handleUpdatePrice(p.id)}
                                  sx={{ width: 120 }}
                                />
                                <Button
                                  size="small"
                                  variant="contained"
                                  disabled={updatingId === p.id}
                                  onClick={() => handleUpdatePrice(p.id)}
                                  sx={{ fontWeight: 700 }}
                                >
                                  Update
                                </Button>
                              </Stack>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                Closed {p.closed_at ? dayjs(p.closed_at).format('HH:mm:ss') : ''}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <IconButton size="small" color="error" onClick={() => handleDeleteSandbox(p.id)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Card>

      {/* ── Simulate Order Modal ─────────────────────────────────────────── */}
      <Dialog open={simModalOpen} onClose={() => !simulating && setSimModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
          <PlayArrow color="primary" /> Test Order Simulation
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Simulate placing an order into the platform's execution pipeline to test tracking, SL calculation, and lifecycle events.
          </Typography>

          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Asset Type</InputLabel>
              <Select
                value={simForm.asset_type}
                label="Asset Type"
                onChange={e => setSimForm({ ...simForm, asset_type: e.target.value })}
              >
                <MenuItem value="EQUITY">Stock Equity (CNC)</MenuItem>
                <MenuItem value="OPTION">Index Option / Sandbox</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Symbol</InputLabel>
              <Select
                value={simForm.symbol}
                label="Symbol"
                onChange={e => setSimForm({ ...simForm, symbol: e.target.value })}
              >
                {SYMBOL_PRESETS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              size="small"
              label="Quantity"
              type="number"
              value={simForm.quantity}
              onChange={e => setSimForm({ ...simForm, quantity: Math.max(1, parseInt(e.target.value, 10) || 1) })}
            />

            <TextField
              fullWidth
              size="small"
              label="Price (Leave blank for Live Spot Price)"
              type="number"
              placeholder="e.g. 500.00"
              value={simForm.price}
              onChange={e => setSimForm({ ...simForm, price: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setSimModalOpen(false)} disabled={simulating}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSimulateTrade}
            disabled={simulating}
            sx={{ fontWeight: 700 }}
          >
            {simulating ? 'Executing...' : 'Execute Test Order'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
